export const PAGE_SIZE = 10;
export const EMPTY_DATE_RANGE = Object.freeze({ start: '', end: '' });

export const FILTERS = ['Todos', 'Ativos', 'Inativos', 'Cliente salão', 'Cliente delivery'];
export const PROFILE_TABS = ['Pedidos', 'Visitas', 'Feedback', 'Reservas', 'Dados'];

export const emptySummary = {
  total_clientes: 0,
  clientes_ativos: 0,
  clientes_inativos: 0,
  ticket_medio_delivery: 0,
  ticket_medio_salao: 0,
  valor_total_pedidos: 0,
};

export function formatMoney(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

export function formatDate(value, fallback = 'N/D') {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat('pt-BR').format(date);
}

export function toDateInputValue(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getEmptyDateRange() {
  return { ...EMPTY_DATE_RANGE };
}

export function getCurrentWeekRange() {
  const now = new Date();
  const day = now.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() + mondayOffset);
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);

  return {
    start: toDateInputValue(start),
    end: toDateInputValue(end),
  };
}

export function getCurrentMonthRange() {
  const now = new Date();
  return {
    start: toDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1)),
    end: toDateInputValue(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
}

export function getCurrentYearRange() {
  const now = new Date();
  return {
    start: toDateInputValue(new Date(now.getFullYear(), 0, 1)),
    end: toDateInputValue(new Date(now.getFullYear(), 11, 31)),
  };
}

export function formatDateInputLabel(value) {
  if (!value) return '';
  return formatDate(`${value}T00:00:00`);
}

export function buildCrmDateParams(dateRange) {
  const params = {};
  if (dateRange?.start) params.data_inicio = dateRange.start;
  if (dateRange?.end) params.data_fim = dateRange.end;
  return params;
}

export function formatPeriodLabel(dateRange) {
  if (dateRange?.start && dateRange?.end) {
    return `${formatDateInputLabel(dateRange.start)} a ${formatDateInputLabel(dateRange.end)}`;
  }
  if (dateRange?.start) return `A partir de ${formatDateInputLabel(dateRange.start)}`;
  if (dateRange?.end) return `Até ${formatDateInputLabel(dateRange.end)}`;
  return 'Todo período';
}

export const WEEKDAYS_PT = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

export const PRODUCT_LABEL_BY_KEY = {
  atendabot: 'AtendaBot',
  atenda_bot: 'AtendaBot',
  reservo: 'Reservo',
  reserva: 'Reservo',
  reservas: 'Reservo',
  clube_de_mesa: 'Clube de Mesa',
  clube_mesa: 'Clube de Mesa',
  fidelidade: 'Clube de Mesa',
  feedback_hero: 'Feedback Hero',
  feedback: 'Feedback Hero',
  one_menu: 'One Menu',
  onemenu: 'One Menu',
  cardapio: 'One Menu',
  cardapio_digital: 'One Menu',
  crm: 'CRM',
};

export function normalizeProductKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' e ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function normalizeProductName(value) {
  const key = normalizeProductKey(value);
  if (!key) return '';
  return PRODUCT_LABEL_BY_KEY[key] || String(value || '').trim();
}

export function uniqueProductNames(values = []) {
  return [...new Set(values.map(normalizeProductName).filter(Boolean))];
}

export function pedidoCanalMeta(type) {
  const t = String(type || '').toLowerCase();
  if (t.includes('deliv')) return { tone: 'good', icon: 'bike', label: 'Delivery' };
  if (t.includes('salão') || t.includes('salao')) return { tone: 'purple', icon: 'salao', label: 'Salão' };
  return { tone: 'neutral', icon: 'other', label: type || 'Outro' };
}

export function feedbackSentimentTone(feedbackClass) {
  const s = String(feedbackClass || '').toLowerCase();
  if (s.includes('posit')) return 'good';
  if (s.includes('negat')) return 'danger';
  return 'warn';
}

export function feedbackSentimentLabel(feedbackClass) {
  const s = String(feedbackClass || '').toLowerCase();
  if (s.includes('posit')) return 'Positivo';
  if (s.includes('negat')) return 'Negativo';
  if (s) return s.charAt(0).toUpperCase() + s.slice(1);
  return 'Sem classificação';
}

export function reservationStatusTone(status) {
  const s = String(status || '').toLowerCase();
  if (s.includes('compare')) return 'good';
  if (s.includes('no-show') || s.includes('cancel') || s.includes('falt')) return 'danger';
  if (s.includes('pend')) return 'warn';
  return 'warn';
}

export const ORDER_STATUS_ALIASES = Object.freeze({
  created: 'iniciado',
  waiting: 'iniciado',
  processing: 'iniciado',
  pending_confirmation: 'iniciado',
  processing_confirmation: 'iniciado',
  confirmado: 'em_preparo',
  confirmed: 'em_preparo',
  ready_to_deliver: 'em_preparo',
  dispatched: 'em_transporte',
  done: 'finalizado',
  concluded: 'finalizado',
  finalizada: 'finalizado',
  canceled: 'cancelado',
  cancelled: 'cancelado',
  failed: 'cancelado',
  pending_cancelled: 'cancelado',
  aberta: 'aberto',
});

export const ORDER_STATUS_LABELS = Object.freeze({
  iniciado: 'Iniciado',
  em_preparo: 'Em preparo',
  em_transporte: 'Em transporte',
  finalizado: 'Finalizado',
  cancelado: 'Cancelado',
  aberto: 'Aberto',
  recebido: 'Recebido',
  pronto: 'Pronto',
  entregue: 'Entregue',
});

export function normalizeOrderStatusKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function humanizeOrderStatus(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function normalizeOrderStatusLabel(value) {
  const key = normalizeOrderStatusKey(value);
  if (!key) return '';
  const statusKey = ORDER_STATUS_ALIASES[key] || key;
  return ORDER_STATUS_LABELS[statusKey] || humanizeOrderStatus(value);
}

export function getOrderStatusLabel(order) {
  return normalizeOrderStatusLabel(order?.statusLabel)
    || normalizeOrderStatusLabel(order?.status)
    || normalizeOrderStatusLabel(order?.rawStatus)
    || 'N/D';
}

// Formato relativo + caption secundária, ideal para KPIs com pouco espaço
export function formatRelativeDate(value) {
  if (!value) return { primary: 'N/D', secondary: '' };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { primary: 'N/D', secondary: '' };

  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((startToday - startDate) / (1000 * 60 * 60 * 24));

  const absDate = new Intl.DateTimeFormat('pt-BR').format(date);
  const timeStr = date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });

  if (diffDays === 0) return { primary: 'Hoje', secondary: `às ${timeStr}` };
  if (diffDays === 1) return { primary: 'Ontem', secondary: `às ${timeStr}` };
  if (diffDays > 1 && diffDays <= 30) {
    return { primary: `Há ${diffDays} dias`, secondary: absDate };
  }
  return { primary: absDate, secondary: timeStr };
}

export function formatLastAction(action) {
  if (!action) return 'Sem interação';
  const days = Number(action.days);
  const when = Number.isFinite(days)
    ? days === 0
      ? 'hoje'
      : days === 1
        ? 'ontem'
        : `há ${days} dias`
    : '';

  if (action.type === 'pedido') return `Pedido ${action.channel || ''}`.trim();
  if (action.type === 'reserva') return 'Reserva';
  if (action.type === 'feedback') return `Feedback ${action.channel || ''}`.trim();
  if (action.type === 'visita') return 'Check-in';
  if (action.type === 'mensagem') return 'Mensagem WhatsApp';
  return action.label || when || 'Interação';
}

export function countOnlineOrders(cliente) {
  const explicit = Number(cliente?.deliveryOrders);
  if (Number.isFinite(explicit)) return explicit;
  return (cliente?.pedidos || []).filter((pedido) => pedido.type === 'Delivery').length;
}

export function countSalonOrders(cliente) {
  const explicit = Number(cliente?.salonOrders);
  if (Number.isFinite(explicit)) return explicit;
  const pedidos = cliente?.pedidos || [];
  return pedidos.filter((pedido) => String(pedido.type || '').toLowerCase().includes('sal')).length;
}

export function countTotalOrders(cliente) {
  const explicit = Number(cliente?.totalOrders);
  if (Number.isFinite(explicit)) return explicit;
  return (cliente?.pedidos || []).length;
}

export function priorityClass(cliente) {
  if (cliente?.status === 'Inativo') return 'hot';
  if ((cliente?.feedbacks || []).some((feedback) => feedback.class === 'negativo')) return 'warm';
  return '';
}

export function normalizePhoneKey(value) {
  let digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('55') && digits.length > 11) digits = digits.slice(2);
  if (digits.length > 11) digits = digits.slice(-11);
  if (digits.length === 11 && digits[2] === '9') {
    return `${digits.slice(0, 2)}${digits.slice(3)}`;
  }
  return digits;
}

export function buildWhatsAppHref(value) {
  let digits = String(value || '').replace(/\D/g, '');
  if (!digits) return null;

  if (digits.startsWith('55') && digits.length > 11) {
    return `https://wa.me/${digits}`;
  }

  if (digits.length > 11) {
    digits = digits.slice(-11);
  }

  if (digits.length >= 10) {
    return `https://wa.me/55${digits}`;
  }

  return null;
}

export function normalizeDisplayName(value) {
  return String(value || '')
    .replace(/[\p{Extended_Pictographic}\uFE0F\u200D]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function isPhoneLikeDisplayName(value) {
  const name = normalizeDisplayName(value);
  if (!name) return false;
  const digits = name.replace(/\D/g, '');
  if (digits.length < 8) return false;
  return name.replace(/[\s()+.-]/g, '') === digits;
}

export function getClienteDisplayName(cliente) {
  const name = normalizeDisplayName(cliente?.name);
  if (!name || isPhoneLikeDisplayName(name)) return 'Cliente';
  const lowerName = name.toLowerCase();
  if (lowerName === 'cliente sem nome') return 'Cliente';
  return name;
}

export function getClienteInitials(cliente) {
  const name = getClienteDisplayName(cliente);
  if (!name || name === 'Cliente') return null;
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Calcula dias desde a última interação a partir da data (fallback quando o backend não envia `days`)
export function getLastActionDays(cliente) {
  const explicit = Number(cliente?.lastAction?.days);
  if (Number.isFinite(explicit)) return explicit;
  const date = cliente?.lastAction?.date;
  if (!date) return null;
  const t = new Date(date).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24));
}

// Health ring colorido em volta do avatar baseado no perfil do cliente
export function getHealthClass(cliente) {
  if (!cliente) return '';
  const status = String(cliente.status || '').toLowerCase();
  const totalSpent = Number(cliente.totalSpent || 0);
  const totalPedidos = countTotalOrders(cliente);
  const lastDays = getLastActionDays(cliente);
  const hasNoSignal = lastDays === null;

  // Status do backend é o sinal mais confiável; respeitamos primeiro
  if (status === 'inativo') return 'health-lost';
  if (lastDays !== null && lastDays > 90) return 'health-lost';
  if (lastDays !== null && lastDays > 30) return 'health-risk';
  if (totalSpent >= 500 || totalPedidos >= 20) return 'health-vip';
  if (status === 'ativo' || totalPedidos > 0) return 'health-active';
  return hasNoSignal ? '' : 'health-active';
}

export const INSIGHT_TONE_STYLE = {
  primary: { color: '#4F46E5', bg: '#EEF2FF' },
  good:    { color: '#0D9488', bg: '#F0FDFA' },
  warn:    { color: '#D97706', bg: '#FFFBEB' },
  danger:  { color: '#E11D48', bg: '#FFF1F2' },
};

export function sortClientes(clientes, sort) {
  const dir = sort.dir === 'asc' ? 1 : -1;
  const valueByKey = {
    name: (cliente) => getClienteDisplayName(cliente),
    online: countOnlineOrders,
    visits: (cliente) => (cliente.visitas || []).length,
    total: (cliente) => cliente.totalSpent || 0,
    action: (cliente) => formatLastAction(cliente.lastAction),
    date: (cliente) => new Date(cliente.lastAction?.date || 0).getTime(),
  };
  const getter = valueByKey[sort.key] || valueByKey.date;

  return [...clientes].sort((a, b) => {
    const av = getter(a);
    const bv = getter(b);
    if (typeof av === 'string' || typeof bv === 'string') {
      return String(av).localeCompare(String(bv), 'pt-BR') * dir;
    }
    return (av - bv) * dir;
  });
}

export function filterClientes(clientes, search, activeFilters) {
  const normalizedSearch = search.trim().toLowerCase();
  const digits = normalizedSearch.replace(/\D/g, '');
  const searchPhoneKey = normalizePhoneKey(normalizedSearch);

  return clientes.filter((cliente) => {
    const phoneCandidates = [
      cliente.phone,
      cliente.telefone,
      cliente.phoneKey,
      ...(cliente.phoneVariants || []),
    ].filter(Boolean);

    const matchesPhone = digits && phoneCandidates.some((phone) => {
      const phoneDigits = String(phone || '').replace(/\D/g, '');
      const phoneKey = normalizePhoneKey(phone);
      return phoneDigits.includes(digits) || Boolean(searchPhoneKey && phoneKey === searchPhoneKey);
    });

    const matchesSearch = !normalizedSearch || [
      getClienteDisplayName(cliente),
      cliente.phone,
      cliente.origin,
      ...(cliente.tags || []),
    ].join(' ').toLowerCase().includes(normalizedSearch) ||
      matchesPhone;

    if (!matchesSearch) return false;
    if (activeFilters.includes('Todos')) return true;

    return activeFilters.every((filter) => {
      if (filter === 'Ativos') return cliente.status === 'Ativo';
      if (filter === 'Inativos') return cliente.status === 'Inativo';
      return (cliente.tags || []).includes(filter);
    });
  });
}

export function applyInsightFilter(list, key) {
  if (!key) return list;
  if (key === 'reativacao') {
    return list.filter((c) => {
      const days = getLastActionDays(c);
      return days !== null && days > 30 && days <= 60;
    });
  }
  if (key === 'ativo') {
    return list.filter((c) => {
      const days = getLastActionDays(c);
      return days !== null && days >= 0 && days <= 7;
    });
  }
  return list;
}

